import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { ArrowLeft, User, ShieldCheck, Star, Play, CheckCircle2, Circle, AlertTriangle, Loader2, FileText, Video, Mic, Sparkles, ClipboardList, Award, GraduationCap, Languages, Wrench, Briefcase, LinkIcon, MessageSquare } from "lucide-react";

const EVIDENCE_TONE = {
  CANDIDATE_REPORTED: "bg-slate-100 text-slate-600 border-slate-200",
  RESUME_SUPPORTED: "bg-blue-50 text-blue-700 border-blue-200",
  STAFFHUB_ASSESSED: "bg-amber-50 text-amber-800 border-amber-300",
  STAFFHUB_VERIFIED: "bg-emerald-50 text-emerald-800 border-emerald-300",
};
const EVIDENCE_LABEL = {
  CANDIDATE_REPORTED: "Candidate reported",
  RESUME_SUPPORTED: "Resume supported",
  STAFFHUB_ASSESSED: "ConnectMyHub assessed",
  STAFFHUB_VERIFIED: "ConnectMyHub verified",
};

// Strips a leading duplicate token (e.g., "Senior Senior Software Engineer" -> "Senior Software Engineer")
function sanitizeHeadline(headline) {
  if (!headline) return "";
  return String(headline).replace(/^(\S+)\s+\1\b/i, "$1").trim();
}

const MEDIA_KIND = {
  VIDEO_INTRODUCTION: { label: "Video Introduction", icon: Video, group: "intro" },
  INTERVIEW_AUDIO_RESPONSE: { label: "Interview Response", icon: Mic, group: "interview" },
  SALES_PITCH: { label: "Sales Pitch", icon: Mic, group: "audition" },
  MOCK_CALL: { label: "Mock Call", icon: Mic, group: "audition" },
  ROLE_AUDITION_AUDIO: { label: "Role Audition (Audio)", icon: Mic, group: "audition" },
  ROLE_AUDITION_VIDEO: { label: "Role Audition (Video)", icon: Video, group: "audition" },
  WORK_SAMPLE_MEDIA: { label: "Work Sample", icon: FileText, group: "sample" },
};

export default function CandidateRecord() {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [media, setMedia] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [gates, setGates] = useState(null);
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null); // media id currently playing

  const load = async () => {
    try {
      const [{data: cd}, {data: md}, {data: ad}] = await Promise.all([
        api.get(`/candidates/${id}`),
        api.get(`/candidates/${id}/media`),
        api.get(`/assessments`, {params:{candidate_id:id}}),
      ]);
      setC(cd); setMedia(md); setAssessments(ad);
      try {
        const { data: g } = await api.get(`/candidates/${id}/qualification-check`, {params:{tier:"StaffHub Qualified"}});
        setGates(g);
      } catch (e) { console.error("qualification-check load failed", e); }
    } catch (e) { console.error("CandidateRecord load failed", e); toast.error(e.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!c) return <Layout><div className="text-sm text-slate-500">Loading…</div></Layout>;

  const scoreMap = [["Communication","score_communication"],["Confidence","score_confidence"],["Specificity","score_specificity"],["Experience","score_experience"],["Education","score_education"],["Curiosity","score_curiosity"],["AI","score_ai"],["Software","score_software"],["Time zone","score_timezone"],["Availability","score_availability"],["Reliability","score_reliability"],["References","score_references"],["Role skills","score_role_skills"],["English written","score_english_written"],["English spoken","score_english_spoken"]];
  const approveMedia = async (mid, approved) => {
    try {
      await api.patch(`/media/${mid}`, {approval_status: approved?"Approved":"Kept Internal", employer_visible: approved});
      toast.success(approved ? "Approved for employer" : "Kept internal");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const requestNew = async (mid) => {
    try { await api.patch(`/media/${mid}`, {approval_status: "New Response Requested"}); toast.success("Request logged"); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const saveNotes = async (notes) => {
    try { await api.patch(`/candidates/${id}`, {recruiter_notes: notes}); toast.success("Saved"); }
    catch (e) { toast.error("Failed"); }
  };
  const qualify = async (tier) => {
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/qualify`, {tier});
      toast.success(`Qualified as ${tier}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const groupedMedia = Object.entries(MEDIA_KIND).reduce((acc, [k]) => {
    acc[k] = media.filter(m => m.media_type === k); return acc;
  }, {});

  const TABS = [
    ["overview","Overview"],["skills","Skills & Tools"],["experience","Experience"],
    ["education","Education & Certifications"],["career","Career Profile"],
    ["assessments","Assessments"],["communication","Communication"],
    ["media","Media & Auditions"],["verification","Verification"],
    ["match","Match History"],["recruiter","Recruiter Review"],
  ];

  return (
    <Layout>
      <div className="mb-4">
        <button onClick={()=>nav(-1)} className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-900" data-testid="record-back"><ArrowLeft size={12}/> Back</button>
      </div>

      {/* Header snapshot */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-4">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="shrink-0">
            {c.photo_url ? (
              <img src={`${API_BASE.replace(/\/api$/,"")}${c.photo_url}`} alt="" data-testid="record-photo" className="w-24 h-24 rounded-lg object-cover border border-slate-200"/>
            ) : (
              <div className="w-24 h-24 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><User size={28}/></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-bold text-slate-900 tracking-tight">{c.first_name} {c.last_name}</h1>
              <TierBadge tier={c.qualification_tier}/>
            </div>
            <div className="text-sm text-slate-500 mt-1">{sanitizeHeadline(c.headline) || c.primary_role}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1.5 mt-3 text-xs">
              <Snap k="Target role" v={c.primary_role}/>
              <Snap k="Seniority" v={c.seniority && c.primary_role && c.primary_role.toLowerCase().startsWith((c.seniority||"").toLowerCase()) ? "" : c.seniority}/>
              <Snap k="Location" v={[c.city,c.country].filter(Boolean).join(", ")}/>
              <Snap k="Time zone" v={c.time_zone || c.utc_offset}/>
              <Snap k="Years experience" v={c.years_experience}/>
              <Snap k="Availability" v={c.earliest_start}/>
              <Snap k="Work arrangement" v={(c.work_arrangement||[]).join(", ")}/>
              <Snap k="Comp/mo" v={c.desired_compensation ? `$${c.desired_compensation.toLocaleString()}` : "—"}/>
            </div>
          </div>
          <div className="shrink-0 text-right space-y-1.5">
            <div className="inline-flex items-center gap-1.5 text-slate-900 font-mono font-bold text-lg"><Star size={16} className="text-amber-500 fill-amber-500"/>{c.overall_staffhub_score?.toFixed(2) || "0.00"}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">ConnectMyHub Score</div>
            <div className="flex gap-1.5 justify-end mt-2 flex-wrap">
              <button data-testid="qualify-qualified" disabled={busy || c.qualification_tier==="StaffHub Qualified" || c.qualification_tier==="StaffHub Premier"} onClick={()=>qualify("StaffHub Qualified")} className="h-8 text-[11px] px-3 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold disabled:opacity-40">Mark Qualified</button>
              <button data-testid="qualify-premier" disabled={busy || c.qualification_tier==="StaffHub Premier"} onClick={()=>qualify("StaffHub Premier")} className="h-8 text-[11px] px-3 rounded border border-amber-400 bg-amber-50 text-amber-900 font-semibold disabled:opacity-40">Mark Premier</button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-5 flex gap-1 overflow-x-auto">
        {TABS.map(([id2,lbl])=>(
          <button key={id2} data-testid={`rec-tab-${id2}`} onClick={()=>setTab(id2)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 ${tab===id2?"border-sky-600 text-slate-900":"border-transparent text-slate-500 hover:text-slate-800"}`}>{lbl}</button>
        ))}
      </div>

      {tab==="overview" && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card title="Professional summary" className="md:col-span-2"><p className="text-sm text-slate-700 whitespace-pre-line">{c.summary || <Empty text="No summary yet."/>}</p></Card>
          <Card title="Qualification gates">
            {!gates ? <Empty text="Not available"/> : (
              <ul className="text-xs space-y-1.5">
                {gates.checks.map((chk,i)=>(
                  <li key={i} className="flex items-start gap-1.5">{chk.ok?<CheckCircle2 size={13} className="text-emerald-600 mt-0.5"/>:<Circle size={13} className="text-slate-300 mt-0.5"/>}<span className={chk.ok?"text-slate-800":"text-slate-500"}>{chk.label}{chk.required && <span className="text-red-500 ml-1">*</span>}</span></li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Contact & links" className="md:col-span-2">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Snap k="Email" v={c.email}/><Snap k="Phone" v={c.phone}/>
              <Snap k="LinkedIn" v={c.linkedin_url ? <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">Profile</a> : "—"}/>
              <Snap k="Portfolio" v={c.portfolio_url ? <a href={c.portfolio_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">Portfolio</a> : "—"}/>
              <Snap k="GitHub" v={c.github_url ? <a href={c.github_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">GitHub</a> : "—"}/>
              <Snap k="Website" v={c.website_url ? <a href={c.website_url} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">Website</a> : "—"}/>
            </div>
          </Card>
          <Card title="Remote-work readiness">
            <div className="text-xs grid grid-cols-2 gap-2">
              <Snap k="US timezone" v={c.us_timezone_availability}/>
              <Snap k="Internet" v={c.internet_speed}/>
              <Snap k="Backup internet" v={c.backup_internet?"Yes":"No"}/>
              <Snap k="Backup power" v={c.backup_power?"Yes":"No"}/>
              <Snap k="Equipment" v={c.equipment}/>
              <Snap k="US hours OK" v={c.willing_us_hours?"Yes":"No"}/>
            </div>
          </Card>
        </div>
      )}

      {tab==="skills" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Professional skills" icon={Wrench}><Tags items={c.skills}/></Card>
          <Card title="Software & tools"><Tags items={c.software_skills}/></Card>
          <Card title="CRM systems"><Tags items={c.crm_experience}/></Card>
          <Card title="AI tools"><Tags items={c.ai_tools}/></Card>
          <Card title="Industry-specific systems"><Tags items={c.industry_systems}/></Card>
          <Card title="Industries"><Tags items={c.industries}/></Card>
          <Card title="Languages" className="md:col-span-2" icon={Languages}>
            {(c.languages_detailed || []).length ? (
              <table className="w-full text-xs"><tbody>{c.languages_detailed.map((l,i)=>(
                <tr key={i} className="border-t border-slate-100 first:border-none"><td className="py-2 font-semibold text-slate-800">{l.language}</td><td className="py-2 text-slate-600">Spoken: {l.spoken_proficiency || "—"}</td><td className="py-2 text-slate-600">Written: {l.written_proficiency || "—"}</td><td className="py-2 text-slate-500">{l.primary?"Primary":""}</td></tr>
              ))}</tbody></table>
            ) : <Tags items={c.languages}/>}
          </Card>
        </div>
      )}

      {tab==="experience" && (
        <Card title="Employment history" icon={Briefcase}>
          {(c.employment_history||[]).length===0 ? <Empty text="No employment history yet."/> :
            <div className="space-y-4">{c.employment_history.map((e,i)=>(
              <div key={i} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div><div className="font-semibold text-slate-900">{e.job_title || "Role"} — {e.employer || "Employer"}</div>
                    <div className="text-xs text-slate-500">{e.industry} · {e.location} · {e.start_date}{e.current?" – Present":e.end_date?` – ${e.end_date}`:""}</div></div>
                  <EvidenceBadge level={e.verification_status || "CANDIDATE_REPORTED"}/>
                </div>
                {e.responsibilities && <div className="mt-2 text-xs text-slate-700"><span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Responsibilities</span><p>{e.responsibilities}</p></div>}
                {e.accomplishments && <div className="mt-2 text-xs text-slate-700"><span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Accomplishments</span><p>{e.accomplishments}</p></div>}
                {e.quantifiable_results && <div className="mt-2 text-xs text-emerald-800"><span className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700">Measurable results</span><p>{e.quantifiable_results}</p></div>}
                {e.management_responsibilities && <div className="mt-2 text-xs text-slate-700"><span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Management</span><p>{e.management_responsibilities}</p></div>}
              </div>
            ))}</div>}
        </Card>
      )}

      {tab==="education" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Education" icon={GraduationCap}>
            {(c.education_history||[]).length===0 ? <Empty text="No education records."/> :
              <div className="space-y-3">{c.education_history.map((e,i)=>(
                <div key={i} className="border border-slate-200 rounded p-3"><div className="font-semibold text-sm text-slate-900">{e.degree || e.degree_level} · {e.field}</div><div className="text-xs text-slate-500">{e.institution} · {e.graduation_date}{e.honors?` · ${e.honors}`:""}</div></div>
              ))}</div>}
          </Card>
          <Card title="Certifications & Licenses" icon={Award}>
            {(c.certifications_detailed||[]).length===0 && (c.certifications||[]).length===0 ? <Empty text="No certifications."/> :
              <div className="space-y-2">{(c.certifications_detailed||[]).map((cx,i)=>(
                <div key={i} className="border border-slate-200 rounded p-3 text-xs"><div className="font-semibold text-slate-900 text-sm">{cx.name}</div><div className="text-slate-500">{cx.issuing_organization} · issued {cx.issue_date}{cx.expiration_date?` · expires ${cx.expiration_date}`:""}</div></div>
              ))}
              {(c.certifications||[]).length>0 && <Tags items={c.certifications}/>}
              </div>}
          </Card>
          <Card title="References" className="md:col-span-2">
            {(c.references||[]).length===0 ? <Empty text="No references yet."/> :
              <table className="w-full text-xs"><thead className="text-[10px] uppercase tracking-widest text-slate-500"><tr><th className="text-left py-2">Name</th><th className="text-left py-2">Relationship</th><th className="text-left py-2">Company</th><th className="text-left py-2">Contact</th><th className="text-left py-2">Permission</th></tr></thead><tbody>{c.references.map((r,i)=>(
                <tr key={i} className="border-t border-slate-100"><td className="py-2 font-semibold">{r.name}</td><td className="py-2">{r.relationship}</td><td className="py-2">{r.company}</td><td className="py-2">{r.email}{r.email&&r.phone?" · ":""}{r.phone}</td><td className="py-2">{r.permission_to_contact?"Yes":"No"}</td></tr>
              ))}</tbody></table>}
          </Card>
        </div>
      )}

      {tab==="career" && (
        <Card title="Career profile">
          {c.career_profile && Object.keys(c.career_profile).length>0 ? (
            <div className="space-y-3">{Object.entries(c.career_profile).map(([k,v])=>v && (
              <div key={k}><div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-0.5">{k.replace(/_/g," ")}</div><p className="text-sm text-slate-800 whitespace-pre-line">{v}</p></div>
            ))}</div>
          ) : <Empty text="Career profile not completed."/>}
        </Card>
      )}

      {tab==="assessments" && (
        <Card title="Assessments" icon={ClipboardList}>
          {assessments.length===0 ? <Empty text="No assessments recorded."/> :
            <table className="w-full text-sm"><thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50"><tr><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Score</th><th className="text-left px-3 py-2">Result</th><th className="text-left px-3 py-2">Evidence</th></tr></thead>
              <tbody>{assessments.map(a=>(
                <tr key={a.id} className="border-t border-slate-100"><td className="px-3 py-2 font-semibold">{a.name}</td><td className="px-3 py-2">{a.type}</td><td className="px-3 py-2 font-mono">{a.score!=null?`${a.score}/${a.max_score}`:"—"}</td><td className="px-3 py-2">{a.passed===true?<span className="text-emerald-700">Passed</span>:a.passed===false?<span className="text-red-700">Not passed</span>:"—"}</td><td className="px-3 py-2"><EvidenceBadge level={a.verification_status==="Recruiter-verified"?"STAFFHUB_ASSESSED":"CANDIDATE_REPORTED"}/></td></tr>
              ))}</tbody>
            </table>}
        </Card>
      )}

      {tab==="communication" && (
        <CommunicationTab c={c} onScored={load}/>
      )}

      {tab==="media" && (
        <div className="space-y-4">
          {Object.entries(MEDIA_KIND).map(([mtype, def])=>{
            const items = groupedMedia[mtype];
            if (!items.length) return null;
            const Ic = def.icon;
            return (
              <Card key={mtype} title={def.label} icon={Ic}>
                <div className="space-y-2">{items.map(m=>(
                  <MediaRow key={m.id} m={m} playing={playing===m.id} onPlay={()=>setPlaying(playing===m.id?null:m.id)} onApprove={approveMedia} onRequestNew={requestNew}/>
                ))}</div>
              </Card>
            );
          })}
          {media.length===0 && <Card title="Media & Auditions"><Empty text="No media submitted yet."/></Card>}
        </div>
      )}

      {tab==="verification" && <VerificationPanel c={c} media={media} assessments={assessments}/>}

      {tab==="match" && (
        <Card title="Match history">
          <Empty text="Match runs are logged from the AI Matching page. Open matching → run for a job order to see this candidate scored."/>
        </Card>
      )}

      {tab==="recruiter" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Vetting scorecard (1–5)">
            <div className="grid grid-cols-2 gap-2 text-xs">{scoreMap.map(([l,k])=>(
              <div key={k} className="flex justify-between px-2 py-1.5 bg-slate-50 rounded border border-slate-100">
                <span className="text-slate-600">{l}</span><span className="font-mono font-semibold">{c[k] || 0}/5</span>
              </div>
            ))}</div>
            <div className="mt-3 p-2.5 rounded bg-amber-50 border border-amber-200 text-xs"><span className="font-semibold">Overall ConnectMyHub Score: </span>{c.overall_staffhub_score?.toFixed(2)} / 5.00</div>
          </Card>
          <Card title="Recruiter notes (internal only)"><NotesEditor initial={c.recruiter_notes||""} onSave={saveNotes}/></Card>
        </div>
      )}
    </Layout>
  );
}

const Card = ({title, icon:Ic, children, className=""}) => (
  <section className={`bg-white border border-slate-200 rounded-xl p-5 shadow-sm ${className}`}>
    <div className="flex items-center gap-2 mb-3">{Ic && <Ic size={14} className="text-sky-600"/>}<h3 className="text-sm font-semibold text-slate-900">{title}</h3></div>
    {children}
  </section>
);
const Snap = ({k,v}) => (<div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k}</div><div className="text-slate-900 text-xs font-medium">{v || "—"}</div></div>);
const Empty = ({text}) => <div className="text-xs text-slate-500 italic">{text}</div>;
const Tags = ({items}) => (items||[]).length===0 ? <Empty text="None on file."/> : <div className="flex flex-wrap gap-1.5">{items.map((s,i)=><span key={i} className="text-[11px] px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">{s}</span>)}</div>;
const EvidenceBadge = ({level}) => (<span className={`inline-flex text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${EVIDENCE_TONE[level] || EVIDENCE_TONE.CANDIDATE_REPORTED}`} data-testid={`ev-${level}`}>{EVIDENCE_LABEL[level] || level}</span>);
const TierBadge = ({tier}) => {
  const tone = tier==="StaffHub Premier"?"bg-amber-50 text-amber-800 border-amber-300":tier==="StaffHub Qualified"?"bg-emerald-50 text-emerald-700 border-emerald-200":"bg-slate-100 text-slate-700 border-slate-200";
  return <span data-testid="tier-badge" className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded border ${tone}`}><ShieldCheck size={11}/> {tier || "Applicant"}</span>;
};

function MediaRow({m, playing, onPlay, onApprove, onRequestNew}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3" data-testid={`media-row-${m.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          {m.question_prompt && <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Question</div>}
          <div className="text-sm text-slate-900 font-medium mb-1">{m.title || m.question_prompt || MEDIA_KIND[m.media_type]?.label}</div>
          <div className="text-[11px] text-slate-500 flex gap-2 flex-wrap items-center">
            <span>{new Date(m.submitted_at).toLocaleDateString()}</span>
            {m.duration && <span>· {m.duration}s</span>}
            {m.size && <span>· {Math.round(m.size/1024)} KB</span>}
            {m.role_family && <span>· {m.role_family}</span>}
            <StatusPill status={m.approval_status}/>
          </div>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button data-testid={`play-${m.id}`} onClick={onPlay} className="h-8 px-3 text-[11px] rounded border border-slate-300 inline-flex items-center gap-1 font-semibold text-slate-700 hover:bg-slate-50"><Play size={11}/>{playing?"Hide":"Play"}</button>
          {m.approval_status !== "Approved" && <button data-testid={`approve-${m.id}`} onClick={()=>onApprove(m.id,true)} className="h-8 px-3 text-[11px] rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold hover:bg-emerald-100">Approve for employer</button>}
          {m.approval_status !== "Kept Internal" && <button onClick={()=>onApprove(m.id,false)} className="h-8 px-3 text-[11px] rounded border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50">Keep internal</button>}
          <button data-testid={`request-new-${m.id}`} onClick={()=>onRequestNew(m.id)} className="h-8 px-3 text-[11px] rounded border border-amber-300 bg-amber-50 text-amber-800 font-semibold hover:bg-amber-100">Request new</button>
        </div>
      </div>
      {playing && (
        <div className="mt-3">
          {m.media_type.includes("VIDEO") || m.media_type==="VIDEO_INTRODUCTION" ? (
            <video controls preload="none" className="w-full max-w-lg rounded border border-slate-200" src={`${API_BASE}/media/${m.id}/stream`} crossOrigin="use-credentials" data-testid={`player-${m.id}`}/>
          ) : m.media_type==="WORK_SAMPLE_MEDIA" && !((m.mime||"").startsWith("audio/") || (m.mime||"").startsWith("video/")) ? (
            <a href={`${API_BASE}/media/${m.id}/stream`} target="_blank" rel="noreferrer" className="text-xs text-sky-700 font-semibold">Open work sample</a>
          ) : (
            <audio controls preload="none" className="w-full max-w-lg" src={`${API_BASE}/media/${m.id}/stream`} crossOrigin="use-credentials" data-testid={`player-${m.id}`}/>
          )}
        </div>
      )}
    </div>
  );
}
const StatusPill = ({status}) => {
  const tone = status==="Approved"?"bg-emerald-50 text-emerald-700 border-emerald-200":status==="Kept Internal"?"bg-slate-100 text-slate-600 border-slate-200":status==="New Response Requested"?"bg-amber-50 text-amber-800 border-amber-300":"bg-sky-50 text-sky-700 border-sky-200";
  return <span className={`inline-flex text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded border ${tone}`}>{status || "Submitted"}</span>;
};

function CommunicationTab({c, onScored}) {
  const initial = c.staffhub_communication_assessment?.components || {spoken_clarity:0,listening:0,grammar:0,conversational:0,professional:0,written:0,confidence:0};
  const [s, setS] = useState(initial);
  const [notes, setNotes] = useState(c.staffhub_communication_assessment?.notes || "");
  const [busy, setBusy] = useState(false);
  const CAPS = {spoken_clarity:20,listening:15,grammar:15,conversational:15,professional:15,written:10,confidence:10};
  const total = Object.entries(s).reduce((sum,[k,v])=>sum+Math.min(CAPS[k]||0,Math.max(0,parseInt(v)||0)),0);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/candidates/${c.id}/communication-assessment`, {candidate_id:c.id, ...s, notes}); toast.success(`Communication scored: ${total}/100`); onScored?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Card title="ConnectMyHub Communication Assessment (recruiter-scored)" icon={MessageSquare}>
      <p className="text-xs text-slate-500 mb-4">This is the gate-eligible score. Candidate self-ratings do NOT count.</p>
      <div className="grid md:grid-cols-2 gap-3">
        {Object.entries(CAPS).map(([k,mx])=>(
          <div key={k} className="flex items-center gap-3">
            <div className="flex-1"><div className="text-xs font-semibold text-slate-800 capitalize">{k.replace(/_/g," ")}</div><div className="text-[10px] text-slate-500">Max {mx}</div></div>
            <input type="number" min={0} max={mx} data-testid={`comm-${k}`} value={s[k]||0} onChange={e=>setS({...s,[k]:Math.min(mx,Math.max(0,parseInt(e.target.value)||0))})} className="w-20 h-9 px-3 rounded border border-slate-300 text-sm font-mono"/>
          </div>
        ))}
      </div>
      <div className="mt-4 p-3 rounded bg-slate-50 border border-slate-200 text-sm flex justify-between"><span className="font-semibold">Total (gate-eligible)</span><span className="font-mono font-bold text-lg">{total} / 100</span></div>
      <textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Recruiter notes on communication…" className="w-full mt-3 px-3 py-2 rounded border border-slate-300 text-sm resize-none"/>
      <button data-testid="comm-save" onClick={save} disabled={busy} className="mt-3 h-10 px-4 rounded bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin"/>} Save assessment</button>
    </Card>
  );
}

function VerificationPanel({c, media, assessments}) {
  // Locally compute a summary similar to backend build_verification_summary
  const emp = (c.employment_history||[]);
  const items = [
    {area:"Identity", status:"PENDING", level:null},
    {area:"Employment", status: emp.some(e=>e.employer)?"COMPLETED":"PENDING", level: emp.some(e=>e.employer)?"RESUME_SUPPORTED":null},
    {area:"Education", status:(c.education_history||[]).length?"COMPLETED":"PENDING", level:(c.education_history||[]).length?"RESUME_SUPPORTED":null},
    {area:"Certifications", status:(c.certifications_detailed||[]).length||(c.certifications||[]).length?"COMPLETED":"NOT_STARTED", level:(c.certifications_detailed||[]).length?"RESUME_SUPPORTED":null},
    {area:"References", status:(c.references||[]).length?"COMPLETED":"PENDING", level:(c.references||[]).length?"CANDIDATE_REPORTED":null},
    {area:"Skills Assessment", status:assessments.some(a=>a.verification_status==="Recruiter-verified")?"ASSESSED":assessments.length?"COMPLETED":"PENDING", level:assessments.some(a=>a.verification_status==="Recruiter-verified")?"STAFFHUB_ASSESSED":null},
    {area:"Communication Assessment", status:(c.staffhub_communication_score||0)>0?"ASSESSED":"PENDING", level:(c.staffhub_communication_score||0)>0?"STAFFHUB_ASSESSED":null},
    {area:"Video Introduction", status: media.find(m=>m.media_type==="VIDEO_INTRODUCTION" && m.approval_status==="Approved")?"VERIFIED": media.find(m=>m.media_type==="VIDEO_INTRODUCTION")?"COMPLETED":"NOT_STARTED", level: media.find(m=>m.media_type==="VIDEO_INTRODUCTION" && m.approval_status==="Approved")?"STAFFHUB_ASSESSED":media.find(m=>m.media_type==="VIDEO_INTRODUCTION")?"CANDIDATE_REPORTED":null},
    {area:"Role Audition", status: media.some(m=>(m.media_type||"").startsWith("ROLE_AUDITION")||["SALES_PITCH","MOCK_CALL","WORK_SAMPLE_MEDIA"].includes(m.media_type))?"COMPLETED":"NOT_STARTED", level:null},
  ];
  const STATUS_TONE = {VERIFIED:"bg-emerald-50 text-emerald-800 border-emerald-300", ASSESSED:"bg-amber-50 text-amber-800 border-amber-300", COMPLETED:"bg-blue-50 text-blue-800 border-blue-200", PENDING:"bg-slate-100 text-slate-600 border-slate-200", NOT_STARTED:"bg-slate-50 text-slate-400 border-slate-200", NOT_VERIFIED:"bg-slate-50 text-slate-500 border-slate-200"};
  return (
    <Card title="Trust & Verification summary" icon={ShieldCheck}>
      <p className="text-xs text-slate-500 mb-4">Only <span className="font-semibold text-emerald-700">VERIFIED</span> means ConnectMyHub actually verified this area. Other statuses reflect completion, not verification.</p>
      <div className="grid md:grid-cols-2 gap-2">
        {items.map((it,i)=>(
          <div key={i} className="flex items-center justify-between border border-slate-200 rounded p-3">
            <div><div className="text-sm font-medium text-slate-800">{it.area}</div>{it.level && <div className="mt-1"><EvidenceBadge level={it.level}/></div>}</div>
            <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${STATUS_TONE[it.status]||STATUS_TONE.PENDING}`} data-testid={`verify-${it.area.replace(/\s+/g,'-').toLowerCase()}`}>{it.status.replace("_"," ")}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function NotesEditor({initial, onSave}){
  const [v,setV]=useState(initial);
  return (<>
    <textarea rows={8} value={v} onChange={e=>setV(e.target.value)} data-testid="recruiter-notes" placeholder="Internal recruiter notes (never shared with employers)…" className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/>
    <button data-testid="save-notes" onClick={()=>onSave(v)} className="mt-2 h-9 px-4 rounded bg-slate-900 text-white text-sm font-semibold">Save notes</button>
  </>);
}
